import { z } from 'zod'
import { DEFAULT_TBANK_COMMISSION_RATE } from '../../constants/investments'

const datePattern = /^\d{4}-\d{2}-\d{2}$/

export const screenOfzRequestSchema = z.object({
  maturityFrom: z.string().regex(datePattern, 'Expected YYYY-MM-DD format'),
  maturityTo: z.string().regex(datePattern, 'Expected YYYY-MM-DD format'),
  accountType: z.enum(['REGULAR', 'IIS3']),
  strategy: z.enum(['YIELD', 'MONTHLY_INCOME']),
  budget: z.number().positive().optional(),
  commissionRate: z.number().min(0).max(0.05).default(DEFAULT_TBANK_COMMISSION_RATE),
  minCouponRatePct: z.number().min(0).optional(),
  maxCouponRatePct: z.number().min(0).optional(),
})
export type ScreenOfzRequestDto = z.infer<typeof screenOfzRequestSchema>

export const screenedBondSchema = z.object({
  uid: z.string(),
  isin: z.string(),
  ticker: z.string(),
  name: z.string(),
  cleanPricePct: z.number(),
  aci: z.number(),
  dirtyPrice: z.number(),
  couponRatePct: z.number(),
  couponsPerYear: z.number().int(),
  nextCouponDate: z.string().nullable(),
  maturityDate: z.string(),
  yearsToMaturity: z.number(),
  grossXirr: z.number(),
  netXirr: z.number(),
  grossApprox: z.number(),
  netApprox: z.number(),
  currentYield: z.number(),
  couponMonths: z.array(z.number().int()), // 1..12
})
export type ScreenedBondDto = z.infer<typeof screenedBondSchema>

export const holdingSchema = z.object({
  uid: z.string(),
  isin: z.string(),
  ticker: z.string(),
  quantity: z.number().int(),
  costPerBond: z.number(),
  totalCost: z.number(),
  cleanPrice: z.number(), // clean price per bond in ₽ (limit-order base; T-Bank PRICE_TYPE_CURRENCY)
  cleanPricePct: z.number(), // clean price as % of nominal (par = 100)
  netXirr: z.number(), // per single bond, after-tax yield to maturity
  currentYield: z.number(), // net coupon income rate (annual coupon after tax ÷ dirty price)
  annualCoupon: z.number(), // gross ₽/yr per single bond
  maturityDate: z.string(), // ISO 8601
  yearsToMaturity: z.number(),
})
export type HoldingDto = z.infer<typeof holdingSchema>

export const aggregateSchema = z.object({
  bondCount: z.number().int(),
  totalCost: z.number(), // includes ACI (in dirtyPrice) + broker commission
  annualCoupon: z.number(), // gross ₽/yr across the basket
  weightedNetYield: z.number(), // cost-weighted mean of held netXirr
})
export type AggregateDto = z.infer<typeof aggregateSchema>

export const monthCellSchema = z.object({
  month: z.number().int(), // 1..12
  payments: z.array(
    z.object({ uid: z.string(), isin: z.string(), ticker: z.string(), amount: z.number() })
  ),
})
export type MonthCellDto = z.infer<typeof monthCellSchema>

export const yieldSelectionSchema = z.object({
  kind: z.literal('YIELD'),
  holdings: z.array(holdingSchema),
  aggregate: aggregateSchema,
})

export const coverageSelectionSchema = z.object({
  kind: z.literal('MONTHLY_INCOME'),
  holdings: z.array(holdingSchema),
  calendar: z.array(monthCellSchema),
  coveredCount: z.number().int(),
  uncoveredMonths: z.array(z.number().int()),
  aggregate: aggregateSchema,
})

export const screenOfzResponseSchema = z.object({
  bonds: z.array(screenedBondSchema),
  selection: z.discriminatedUnion('kind', [yieldSelectionSchema, coverageSelectionSchema]),
})
export type ScreenOfzResponseDto = z.infer<typeof screenOfzResponseSchema>
